import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { Search, Check, X, Ban, Users, Pencil } from "lucide-react";

const TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Ativos" },
  { key: "blocked", label: "Bloqueados" },
];

export default function Residents() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["mgr-residents", tab, q], queryFn: async () => (await api.get("/residents", { params: { status: tab, q } })).data });

  const act = async (id, action) => {
    try {
      await api.post(`/residents/${id}/${action}`);
      qc.invalidateQueries({ queryKey: ["mgr-residents"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success(action === "approve" ? "Morador aprovado!" : action === "reject" ? "Cadastro recusado." : "Morador bloqueado.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const initials = (n) => n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Moradores</h1>
        <p className="text-sm text-muted-foreground">Aprove cadastros e gerencie a comunidade.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 glass rounded-full p-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`res-tab-${t.key}`} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "brand-gradient text-white" : "text-muted-foreground"}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex-1 flex items-center gap-2 glass rounded-full px-4">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="res-search" placeholder="Buscar por nome, apto, torre, telefone..." className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
        </div>
      </div>

      {isLoading ? <Loading /> : !data?.length ? <EmptyState icon={Users} title="Nenhum morador aqui" /> : (
        <div className="grid gap-3">
          {data.map((r) => (
            <div key={r.id} data-testid="resident-row" className="glass rounded-2xl p-4 flex items-center gap-3 card-hover">
              <div className="w-11 h-11 rounded-full brand-gradient flex items-center justify-center text-white font-semibold shrink-0">{initials(r.name)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground truncate">{r.tower} · Ap {r.unit} · {r.email}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEdit(r)} data-testid="edit-resident-btn" className="p-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"><Pencil className="w-4 h-4" /></button>
                {tab === "pending" && (
                  <>
                    <button onClick={() => act(r.id, "approve")} data-testid="approve-btn" className="p-2 rounded-full bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"><Check className="w-4 h-4" /></button>
                    <button onClick={() => act(r.id, "reject")} data-testid="reject-btn" className="p-2 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors"><X className="w-4 h-4" /></button>
                  </>
                )}
                {tab === "approved" && (
                  <button onClick={() => act(r.id, "block")} data-testid="block-btn" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-white/5 border border-white/10 text-muted-foreground hover:text-red-400 transition-colors"><Ban className="w-3.5 h-3.5" /> Bloquear</button>
                )}
                {tab === "blocked" && (
                  <button onClick={() => act(r.id, "approve")} data-testid="unblock-btn" className="text-xs px-3 py-2 rounded-full bg-emerald-500/15 text-emerald-400">Reativar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEdit(null)} />
          <div className="relative w-full max-w-sm glass-strong border border-white/10 rounded-3xl p-6 float-in" data-testid="edit-resident-modal">
            <div className="flex items-center justify-between mb-4"><p className="font-display font-bold">Editar morador</p><button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              {["name", "phone", "tower", "unit"].map((k) => (
                <div key={k}>
                  <label className="text-xs text-muted-foreground capitalize">{{ name: "Nome", phone: "Celular", tower: "Torre", unit: "Apartamento" }[k]}</label>
                  <input value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`edit-${k}`} className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors" />
                </div>
              ))}
              <button
                onClick={async () => {
                  try {
                    await api.put(`/residents/${edit.id}`, { name: edit.name, phone: edit.phone, tower: edit.tower, unit: edit.unit });
                    qc.invalidateQueries({ queryKey: ["mgr-residents"] });
                    setEdit(null);
                    toast.success("Cadastro atualizado.");
                  } catch (e) { toast.error(apiError(e)); }
                }}
                data-testid="edit-resident-save"
                className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform"
              >Salvar alterações</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
